import { useState } from 'react'
import { useNavigation } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { GenericNavigationProp } from '@navigation'
import { useSignIn, useUpdateProfile } from '@hooks'
import { useAuthCredentialsStore, useCurrentUserStore } from '@store'
import { getCurrentPosition, normalizeEmail, translateApiError } from '@utils'
import { useLanguage } from '@context'

interface useLoginResult {
  email: string
  password: string
  isPending: boolean
  error: string
  setEmail: React.Dispatch<React.SetStateAction<string>>
  setPassword: React.Dispatch<React.SetStateAction<string>>
  handleSignIn: () => void
}

export const useLogin = (): useLoginResult => {
  const navigation = useNavigation<GenericNavigationProp>()
  const { t } = useLanguage()
  const queryClient = useQueryClient()
  const { mutate: signInMutation, isPending } = useSignIn()
  const { mutate: updateProfileMutation } = useUpdateProfile()
  const { setAuthCredentials } = useAuthCredentialsStore()
  const { setUserID } = useCurrentUserStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  const handleSignIn = () => {
    setError('')

    // Trailing space / capitalisation from the keyboard breaks the API's @IsEmail
    // (→ 400). Password is intentionally NOT normalised.
    const cleanedEmail = normalizeEmail(email)

    if (!cleanedEmail || !password) {
      setError(t('login.error.noInfo'))
      return
    }

    signInMutation(
      { email: cleanedEmail, password },
      {
        onSuccess: async (user) => {
          setAuthCredentials(cleanedEmail)
          // The token is now set, so authed queries become enabled. Refresh the
          // Premium status for the account that just signed in (logout clears
          // the whole cache, so this is the login half of the auth lifecycle).
          void queryClient.invalidateQueries({ queryKey: ['subscription'] })
          if (!user.account.isOnboardingCompleted) {
            (navigation as any).navigate('Auth', { screen: 'RegistrationSteps' })
          } else {
            setUserID(user.account.id)
            const coords = await getCurrentPosition()
            if (coords) {
              updateProfileMutation({ latitude: coords.latitude, longitude: coords.longitude, _silent: true })
            }
          }
        },
        onError: (err) => {
          const { message } = translateApiError(t, err)
          setError(message)
        },
      },
    )
  }

  return {
    email,
    password,
    isPending,
    error,
    setEmail,
    setPassword,
    handleSignIn,
  }
}
